import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import isNil from "lodash/isNil";
import { useLocation } from "react-router-dom";

import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import Button from "@material-ui/core/Button";

import IProduct, { IFoodlaCategory } from "models/product";
import { IFoodlaCategoryOption } from "models/category";
import ListDropDown from "components/inputfields/ListDropDown";
import InputLabel from "components/inputfields/InputLabel";
import { TAX_CLASSIFICATION, TEMPORARY_FOOD_VAT_LABEL } from "components/constants-ts";
import { isBeverages, isMagazine, isNewFood, isNewNonFood, isSnusOrTobacco } from "./category";
import { isBlank } from "utils/helpers-ts";
import ThreeWaySwitch from "components/inputfields/ThreeWaySwitch";
import { useIsFieldRequired } from '../validation-context/ProductValidationContext';

interface IBasicDataProps {
  readOnly?: boolean;
  approve?: boolean;
  state: IProduct;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
  rootCategory?: IFoodlaCategoryOption;
}

const toNum = (value: any) => {
  if (isNil(value)) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    if (isBlank(value)) return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

const checkUseTemporaryFoodVAT = (state: IProduct, rootCategory?: IFoodlaCategoryOption) => {
  const isSnusOrTobaccoCategory = isSnusOrTobacco(state.foodlaCategory, rootCategory);
  const isFood = isNewFood(state.foodlaCategory, rootCategory);

  return (isFood && !isSnusOrTobaccoCategory);
}

export const calculateTax = (
  rootCategory?: IFoodlaCategoryOption,
  foodlaCategory?: IFoodlaCategory,
  alcoholPercentage?: IProduct['alcoholPercentage']
) => {
  const isSnusOrTobaccoCategory = isSnusOrTobacco(foodlaCategory, rootCategory);
  const isMagazineCategory = isMagazine(foodlaCategory, rootCategory);
  const isBeveragesCategory = isBeverages(foodlaCategory, rootCategory);
  const isFood = isNewFood(foodlaCategory, rootCategory);
  const isNonFood = isNewNonFood(foodlaCategory, rootCategory);
  const alcohol = toNum(alcoholPercentage);
  const isStrongAlcohol = isBeveragesCategory && !isNil(alcohol) && alcohol > 3.5;

  if (isSnusOrTobaccoCategory) return 25;
  if (isMagazineCategory) return 6;
  if (isStrongAlcohol) return 25;
  if (isFood) return 12;
  if (isNonFood) return 25;
  return 12;
};

// Same events as BasicData: a chosen category changes, or a Drycker product crosses the 3.5% alcohol threshold.
export const isTaxRecalculationEvent = (
  rootCategory: IFoodlaCategoryOption | undefined,
  before: Pick<IProduct, 'foodlaCategory' | 'alcoholPercentage'>,
  after: Pick<IProduct, 'foodlaCategory' | 'alcoholPercentage'>
) => {
  const categoryId = after.foodlaCategory?.id;
  if (isBlank(categoryId)) return false;
  const isStrongAlcohol = (product: typeof before) => {
    const alcohol = toNum(product.alcoholPercentage);
    return isBeverages(product.foodlaCategory, rootCategory) && !isNil(alcohol) && alcohol > 3.5;
  };
  return before.foodlaCategory?.id !== categoryId || isStrongAlcohol(before) !== isStrongAlcohol(after);
};

const BasicData = ({ readOnly, approve, state, setState, rootCategory }: IBasicDataProps) => {
  const isTaxRequired = useIsFieldRequired('basicDataTaxClassification');
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  const rCount = renderCountRef.current;

  const [mounted, setMounted] = useState(false);
  const [trigger, setTrigger] = useState(false);
  const [tax, setTax] = useState<number|null>(null);
  
  const prevCategoryRef = useRef<string | null>();
  const prevStrongAlcoholRef = useRef<boolean>();
  const isFirstRunRef = useRef(true);

  const location = useLocation();
  const locationKeyRef = useRef(location.key);
  
  // console.log(`[BasicData][#${rCount}] Location check:`, {
  //   locationKeyRefCurrent: locationKeyRef.current,
  //   locationKey: location.key,
  //   willReset: locationKeyRef.current !== location.key
  // });
  
  // Reset on actual navigation (location key changes)
  if (locationKeyRef.current !== location.key) {
    // console.log(`[BasicData][#${rCount}] Location changed from ${locationKeyRef.current} to ${location.key}, resetting refs`);
    locationKeyRef.current = location.key;
    isFirstRunRef.current = true;
    prevCategoryRef.current = undefined;
    prevStrongAlcoholRef.current = undefined;
  }

  const stateExists = !!state;
  const rootCategoryExists = !!rootCategory;
  const hasSetState = typeof setState === 'function';
  const isFirstRender = rCount < 2;
  
  // console.log(`[BasicData][#${rCount}] isFirstRender calc:`, { rCount, isFirstRender, result: rCount < 2 });
  
  useEffect(() => {
    // console.log(`[BasicData][#${rCount}] Mount effect running:`, { 
    //   stateExists, 
    //   rootCategoryExists, 
    //   hasSetState, 
    //   isFirstRender,
    //   willMount: stateExists && rootCategoryExists && hasSetState && !isFirstRender
    // });
    
    if (stateExists && rootCategoryExists && hasSetState && !isFirstRender) {
      setMounted(true);
      // console.log(`[BasicData][#${rCount}] Mounted successfully on render`);
    } else {
      // console.log(`[BasicData][#${rCount}] Missing dependencies, cannot mount:`, { stateExists, rootCategoryExists, hasSetState, rCount, isFirstRender });
      isFirstRunRef.current = true;
      setMounted(false);
    }
  }, [rootCategoryExists, stateExists, hasSetState, isFirstRender]);

  useEffect(() => {
    if (mounted) {
      const newTax = toNum(state.basicDataTaxClassification);
      setTax(newTax);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  const isBeveragesCategory = isBeverages(state.foodlaCategory, rootCategory);

  const _categoryId = state?.foodlaCategory?.id;
  const foodlaCategoryId = isBlank(_categoryId) ? null : _categoryId!;
  const _alcoholPercentage = toNum(state.alcoholPercentage);
  const isStrongAlcohol = isBeveragesCategory && !isNil(_alcoholPercentage) && _alcoholPercentage! > 3.5;

  const taxClassificationOptions = useMemo(() => {
    if (checkUseTemporaryFoodVAT(state, rootCategory)) {
      return TAX_CLASSIFICATION.map((item) => {
        if (item.value !== 12) return item;
        return { ...item, displayName: TEMPORARY_FOOD_VAT_LABEL };
      });
    }
    return TAX_CLASSIFICATION;
  }, [state, rootCategory]);

  useEffect(() => {
    if (!mounted) return;
    
    // console.log(`[BasicData][#${rCount}] Change detection running:`, {
    //   isFirstRun: isFirstRunRef.current,
    //   prevCategory: prevCategoryRef.current,
    //   currentCategory: foodlaCategoryId,
    //   prevAlcohol: prevStrongAlcoholRef.current,
    //   currentAlcohol: isStrongAlcohol,
    // });
    
    // Skip initial load
    if (isFirstRunRef.current) {
      // console.log(`[BasicData][#${rCount}] First run detected, setting isFirstRunRef to FALSE and storing values`);
      isFirstRunRef.current = false;
      prevCategoryRef.current = foodlaCategoryId;
      prevStrongAlcoholRef.current = isStrongAlcohol;
      return;
    }
    
    // Check if category or alcohol changed
    const categoryChanged = prevCategoryRef.current !== foodlaCategoryId;
    const alcoholChanged = prevStrongAlcoholRef.current !== isStrongAlcohol;
    
    // console.log(`[BasicData][#${rCount}] Checking for changes:`, { categoryChanged, alcoholChanged });
    
    if (categoryChanged || alcoholChanged) {
      // console.log(`[BasicData][#${rCount}] CHANGE DETECTED! Setting trigger to true`);
      if (foodlaCategoryId) {
        setTrigger(true);
      }
      prevCategoryRef.current = foodlaCategoryId;
      prevStrongAlcoholRef.current = isStrongAlcohol;
    }
  }, [foodlaCategoryId, isStrongAlcohol, mounted]);

  // Snackbar state (message + open) and key to force re-mount for back-to-back messages
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: 'info' | 'success' | 'warning' | 'error' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [snackbarKey, setSnackbarKey] = useState(0);
  // Show MUI Snackbar for 5s
  const showSnackbar = (message: string, severity: 'info' | 'success' | 'warning' | 'error' = 'info') => {
    // Force Snackbar to restart timer even if already open
    setSnackbarKey(k => k + 1);
    setSnackbar({ open: true, message, severity });
  };
  // Close handler (ignore clickaway per MUI docs)
  const handleSnackbarClose = (_: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  // Snackbar action click: close and focus the tax classification dropdown
  const handleSnackbarActionClick = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
    // Focus after closing to avoid focus stealing by Snackbar unmount animation
    setTimeout(() => {
      const taxClassificationElement = document.getElementById('basicDataTaxClassification');
      if (taxClassificationElement) {
        taxClassificationElement.focus();
      }
    }, 100);
  };

  const handleDropdownChange = useCallback((
    event: React.ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>,
  ) => {
      const stateName = event.target.name;
      const stateValue = event.target.value;

      if (!stateName) return;
      const value = toNum(stateValue);
      setTax(value);
      setTrigger(false);
  }, []);

  const calculatedTax = useMemo(() => {
    return calculateTax(rootCategory, state.foodlaCategory, state.alcoholPercentage);
  }, [state.foodlaCategory, rootCategory, state.alcoholPercentage]);

  useEffect(() => {
    if (!mounted) return;
    if (!trigger && !isNil(tax)) return;
    // Skip recalculation on initial load if basicDataTaxClassification already exists
    if (!trigger && !isNil(state.basicDataTaxClassification)) return;

    // console.log(`[BasicData][#${rCount}] Snackbar effect running:`, {
    //   trigger,
    //   tax,
    //   calculatedTax,
    //   taxMismatch: calculatedTax !== tax
    // });

    if (calculatedTax !== tax) {
      if (trigger && !isNil(tax)) {
        // console.log(`[BasicData][#${rCount}] SHOWING SNACKBAR - trigger: ${trigger}, calculatedTax: ${calculatedTax}, tax: ${tax}`);
        showSnackbar(`Produktens skattesats ändrades till ${calculatedTax}% - du kan justera detta manuellt`, 'info');
      }
      // setState(oldState => ({ ...oldState, basicDataTaxClassification: calculatedTax}));
      setTax(calculatedTax);
    }
  }, [calculatedTax, mounted, setState, tax, trigger]);

  useEffect(() => {
    if (!mounted) return;
    if (isNil(tax)) return;

    setState(oldState => ({ ...oldState, basicDataTaxClassification: tax }));
  }, [mounted, setState, tax]);

  return (
    <>
      <InputLabel
        heading="Produktens skattesats"
        required={isTaxRequired}
      />
      <div style={{ display: 'flex', width: '100%', alignItems: 'start' }}>
        <ListDropDown
          readOnly={readOnly}
          disableLabel={true}
          listData={taxClassificationOptions}
          value={tax}
          targetName="basicDataTaxClassification"
          handleChange={(e) => {
            handleDropdownChange(e);
          }}
          style={{ flexGrow: 1 }}
        />
        {approve && state?.approve?.find(x => x.field === 'basicDataTaxClassification') && (
          <ThreeWaySwitch
            readOnly={readOnly}
            state={state}
            setState={setState}
            name="basicDataTaxClassification"
            style={{ marginTop: 8 }}
          />
        )}
      </div>
      {!readOnly && <Snackbar
        key={snackbarKey}
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity || 'info'}
          style={{ backgroundColor: '#e8eaf6', color: '#4050b5' }}
             action={
              <Button
                color="inherit"
                size="small"
                onClick={handleSnackbarActionClick}
                style={{ fontWeight: 600 }}
              >
                ÄNDRA
              </Button>
            }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>}
  </>
)};

export default BasicData;
